import { clsx } from "clsx";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import {
  BigCursor,
  DarkCursor,
  Dectionary,
  HideImage,
  KeyNavigation,
  MuteSound,
  PageStructure,
  ReadingGuide,
  ReadingMask,
  ScreenReader,
  VoiceNavigation,
} from "../../assets";
import PauseAnimation from "../../assets/Frame 2996-9/pauseanimation.png";
import screenreaders from "../../assets/Frame 2996-9/screenreader.png";
import { useSettings } from "../../context/SettingsContext";
import AccessibilityTooltipButton from "../Buttons/AccessibilityTooltipButton";
import BlueColorModel from "./BlueColorModel";
import ContrastModel from "./ContrastModelform";
import "./ContrastModelform.css";
import KeyNavigationModel from "./KeyNavigationModel";
import {
  postFeatureSelectMessage,
  postFeatureUnselectMessage,
  postNavigationVoiceStartMessage,
  postNavigationPageStructureMessage,
} from "../../utils/message.helper";
import { Feature, Features } from "../../constants/features";
import { useKeyboardShortcuts } from "../../hooks";
import { BaseOption } from "../../types/options";

interface FlyoutGridOption extends BaseOption {
  icon: React.ReactNode | string;
  functionKey: string;
}

// Define the props interface for the component
interface FlyoutAccessibilityGridProps {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Define grid items array with title, label, icon, and setting key
const gridItems: FlyoutGridOption[] = [
  {
    id: Features.NAVIGATION_SCREEN_READER,
    label: "screen_reader",
    icon: screenreaders,
    functionKey: "screenReader",
  },
  {
    id: Features.NAVIGATION_VOICE_NAVIGATION,
    label: "voice_navigation",
    icon: <VoiceNavigation />,
    functionKey: "voiceNavigation",
  },
  {
    id: Features.NAVIGATION_PAUSE_ANIMATION,
    label: "pause_animation",
    icon: PauseAnimation,
    functionKey: "pauseAnimation",
  },
  {
    id: Features.NAVIGATION_HIDE_IMAGES,
    label: "hide_images",
    icon: <HideImage />,
    functionKey: "hideImages",
  },
  {
    id: Features.NAVIGATION_BIG_CURSOR,
    label: "big_cursor",
    icon: <BigCursor />,
    functionKey: "cursor",
  },
  {
    id: Features.NAVIGATION_DARK_CURSOR,
    label: "dark_cursor",
    icon: <DarkCursor />,
    functionKey: "cursor",
  },
  {
    id: Features.NAVIGATION_MUTE_SOUND,
    label: "mute_sound",
    icon: <MuteSound />,
    functionKey: "muteSound",
  },
  {
    id: Features.NAVIGATION_READING_MASK,
    label: "reading_mask",
    icon: <ReadingMask />,
    functionKey: "cursor",
  },
  {
    id: Features.NAVIGATION_READING_GUIDE,
    label: "reading_guide",
    icon: <ReadingGuide />,
    functionKey: "cursor",
  },
  {
    id: Features.NAVIGATION_PAGE_STRUCTURE,
    label: "page_structure",
    icon: <PageStructure />,
    functionKey: "pageStructure",
  },
  /*{
    id: Features.NAVIGATION_DICTIONARY,
    label: "dictionary",
    icon: <Dectionary />,
    functionKey: "dictionary",
  },*/ // Note: Deactivated dictionary at the moment because it only supports English.
  {
    id: Features.NAVIGATION_KEY_NAVIGATION,
    label: "key_navigation",
    icon: <KeyNavigation />,
    functionKey: "keyNavigation",
  },
];

// Main component definition
const FlyoutAccessibilityGrid: React.FC<FlyoutAccessibilityGridProps> = ({
  setIsModalOpen,
}) => {
  // State for window width
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  // Access settings from context
  const {
    oversizeWidget,
    selectedLanguage,
    selectedCheckBox,
    widgetButtonColor,
    buttonColor,
    buttonTextColor,
    widgetDataSettings,
  } = useSettings();

  const mainMenuSettings = widgetDataSettings?.settings?.advanced?.functions;

  // Filter grid items based on mainMenuSettings - moved outside component for stability
  const activeGridItems = React.useMemo(
    () =>
      gridItems.filter((item) => mainMenuSettings?.[item.functionKey] === true),
    [mainMenuSettings]
  );

  // State for selected items, initialized from localStorage with proper type checking
  const [selectedItems, setSelectedItems] = useState<Feature[]>(() => {
    try {
      const stored = localStorage.getItem("selectedItems");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // State for current slides with proper initialization
  const [currentSlides, setCurrentSlides] = useState<{ [key: string]: number }>(
    () => {
      try {
        return activeGridItems.reduce((acc, item) => {
          const storedSlideIndex = localStorage.getItem(
            `slideIndex_${item.id}`
          );
          return {
            ...acc,
            [item.id]: storedSlideIndex ? JSON.parse(storedSlideIndex) : 0,
          };
        }, {});
      } catch {
        return {};
      }
    }
  );

  // Memoized filtered options
  const filteredOptions = React.useMemo(() => {
    return activeGridItems.filter((item) => {
      if (windowWidth <= 768) {
        const mobileHiddenItems: Feature[] = [
          Features.NAVIGATION_READING_MASK,
          Features.NAVIGATION_READING_GUIDE,
          Features.NAVIGATION_KEY_NAVIGATION,
          Features.NAVIGATION_BIG_CURSOR,
          Features.NAVIGATION_DARK_CURSOR,
        ];
        return !mobileHiddenItems.includes(item.id as Feature);
      }
      return true;
    });
  }, [activeGridItems, windowWidth]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Effect to persist state changes
  useEffect(() => {
    if (selectedItems.length > 0) {
      localStorage.setItem("selectedItems", JSON.stringify(selectedItems));
    } else {
      localStorage.removeItem("selectedItems");
    }
  }, [selectedItems]);

  useEffect(() => {
    Object.entries(currentSlides).forEach(([id, index]) => {
      if (index > 0) {
        localStorage.setItem(`slideIndex_${id}`, JSON.stringify(index));
      } else {
        localStorage.removeItem(`slideIndex_${id}`);
      }
    });
  }, [currentSlides]);

  // State for modal visibility
  const [isContrastModalOpen, setIsContrastModalOpen] = useState(false);
  const [isKeyNavigationModalOpen, setIsKeyNavigationModalOpen] =
    useState(false);
  const [isPageStructureModalOpen, setIsPageStructureModalOpen] =
    useState(false);
  const [isBlueColorModalOpen, setIsBlueColorModalOpen] = useState(false);

  // Translation hook
  const { t } = useTranslation();

  const isMac = navigator.platform.toLowerCase().includes("mac");
  const modifierKey = isMac ? "Option" : "Alt";

  // Helper function to get tooltip data for each item
  const getTooltipData = (label: string) => {
    const tooltipData = {
      pause_animation: {
        title: "pause_animation",
        description: "pauses_all_animation_on_the_page",
        shortcutKey: `${modifierKey} + Ctrl + P`,
      },
      voice_navigation: {
        title: "voice_navigation",
        description: "navigate_the_page_using_voice_commands",
        shortcutKey: `${modifierKey} + Ctrl + V`,
      },
      dictionary: {
        title: "dictionary",
        description: "access_a_dictionary_shows_definition_of_selected_texts",
        shortcutKey: `${modifierKey} + Ctrl + K`,
      },
      hide_images: {
        title: "hide_images",
        description: "all_images_on_the_website_are_hidden",
        shortcutKey: `${modifierKey} + Ctrl + I`,
      },
      big_cursor: {
        title: "big_cursor",
        description: "enlarged_white_cursor_for_better_visibility",
        shortcutKey: `${modifierKey} + Ctrl + U`,
      },
      reading_mask: {
        title: "reading_mask",
        description: "mask_text_to_support_focused_reading",
        shortcutKey: `${modifierKey} + Ctrl + W`,
      },
      dark_cursor: {
        title: "dark_cursor",
        description: "enlarged_black_cursor_for_better_visibility",
        shortcutKey: `${modifierKey} + Ctrl + T`,
      },
      mute_sound: {
        title: "mute_sound",
        description: "disable_sound_on_website",
        shortcutKey: `${modifierKey} + Ctrl + G`,
      },
      reading_guide: {
        title: "reading_guide",
        description: "focus_on_one_line_at_a_time_to_support_reading",
        shortcutKey: `${modifierKey} + Ctrl + F`,
      },
      page_structure: {
        title: "page_structure",
        description: "understand_the_layout_of_the_page_for_easier_navigation",
        shortcutKey: `${modifierKey} + Ctrl + P`,
      },
      key_navigation: {
        title: "key_navigation",
        description: "navigate_the_page_using_your_keyboard_and_shortcuts",
        shortcutKey: `${modifierKey} + Ctrl + N`,
      },
    };
    return tooltipData[label as keyof typeof tooltipData];
  };

  useKeyboardShortcuts([
    { key: "s", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_SCREEN_READER) },
    {
      key: "p",
      altKey: true,
      ctrlKey: true,
      callback: () => {
        handleItemClick(Features.NAVIGATION_PAUSE_ANIMATION);
        postFeatureSelectMessage(Features.NAVIGATION_PAUSE_ANIMATION);
      },
    },
    { key: "v", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_VOICE_NAVIGATION) },
    { key: "k", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_DICTIONARY) },
    { key: "i", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_HIDE_IMAGES) },
    { key: "u", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_BIG_CURSOR) },
    { key: "w", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_READING_MASK) },
    { key: "t", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_DARK_CURSOR) },
    { key: "g", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_MUTE_SOUND) },
    { key: "f", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_READING_GUIDE) },
    { key: "p", altKey: true, ctrlKey: true, callback: () => handleItemClick(Features.NAVIGATION_PAGE_STRUCTURE) },
    {
      key: "n",
      altKey: true,
      ctrlKey: true,
      callback: () => setIsKeyNavigationModalOpen((prev) => !prev),
    },
  ]);

  // Effect for handling reset message from parent window
  useEffect(() => {
    const handleReset = (event: MessageEvent) => {
      if (event.data?.action === "RESET") {
        selectedItems.forEach((item) => postFeatureUnselectMessage(item));
        setSelectedItems([]);
        setCurrentSlides(
          activeGridItems.reduce(
            (acc, item) => ({ ...acc, [item.id]: 0 }),
            {}
          )
        );
        activeGridItems.forEach((item) =>
          localStorage.removeItem(`slideIndex_${item.id}`)
        );
        setIsContrastModalOpen(false);
        setIsKeyNavigationModalOpen(false);
        setIsPageStructureModalOpen(false);
        setIsBlueColorModalOpen(false);
      }
    };
    window.addEventListener("message", handleReset);
    return () => window.removeEventListener("message", handleReset);
  }, []);

  // Handle item click logic
  const handleItemClick = (id: Feature) => {
    if (id === Features.COLOR_CONTRAST_MODE) {
      setIsContrastModalOpen(true);
    } else if (id === Features.NAVIGATION_KEY_NAVIGATION) {
      // Toggle the modal instead of just opening it
      setIsKeyNavigationModalOpen((prev) => !prev);
      return;
    } else if (selectedItems.includes(id)) {
      // Unselect "Voice Navigation"
      postFeatureUnselectMessage(id);
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    } else if (id == Features.NAVIGATION_VOICE_NAVIGATION) {
      // Select "Voice Navigation"
      postNavigationVoiceStartMessage(
        selectedLanguage?.code
          ? selectedLanguage?.code
          : widgetDataSettings?.settings?.advanced?.defaultLanguage
      );
      setSelectedItems((prev) => [...prev, id]);
      return;
    }

    if (id === Features.NAVIGATION_PAGE_STRUCTURE) {
      postNavigationPageStructureMessage();
    } else if (id === Features.NAVIGATION_TOOLTIPS) {
      // setIsBlueColorModalOpen(true);
    } else if (id === Features.CONTENT_HIGHLIGHT_LINKS) {
      document.querySelectorAll("a").forEach((link) => {
        const currentColor = (link as HTMLElement).style.backgroundColor;
        (link as HTMLElement).style.backgroundColor =
          currentColor === "yellow" ? "" : "yellow";
      });
    } else {
      let newSelectedItems = selectedItems.includes(id)
        ? selectedItems.filter((item) => item !== id)
        : [...selectedItems, id];

      const exclusiveItems: Feature[] = [
        Features.NAVIGATION_BIG_CURSOR,
        Features.NAVIGATION_DARK_CURSOR,
        Features.NAVIGATION_READING_MASK,
        Features.NAVIGATION_READING_GUIDE,
      ];

      if (exclusiveItems.includes(id)) {
        if (selectedItems.includes(id)) {
          newSelectedItems = newSelectedItems.filter((item) => item !== id);
          postFeatureUnselectMessage(id);
        } else {
          newSelectedItems = newSelectedItems.filter(
            (item) => !exclusiveItems.includes(item)
          );
          exclusiveItems.forEach((item) => {
            if (selectedItems.includes(item) && item !== id) {
              postFeatureUnselectMessage(item);
            }
          });
          newSelectedItems.push(id);
          postFeatureSelectMessage(id);
        }
      } else {
        if (selectedItems.includes(id)) {
          postFeatureUnselectMessage(id);
          if (id === Features.PROFILE_BLIND) postFeatureUnselectMessage(Features.NAVIGATION_SCREEN_READER);
        } else {
          postFeatureSelectMessage(id);
          if (id === Features.PROFILE_BLIND) postFeatureSelectMessage(Features.NAVIGATION_SCREEN_READER);
        }
      }

      setSelectedItems(newSelectedItems);
      localStorage.setItem("selectedItems", JSON.stringify(newSelectedItems));
    }
  };

  // Handle dot click for sliders
  const handleDotClick = (action: Feature, id: Feature, index: number) => {
    postFeatureSelectMessage(action);
    setCurrentSlides((prev) => ({ ...prev, [id]: index }));
    localStorage.setItem(
      "currentSlide",
      JSON.stringify({ ...currentSlides, [id]: index })
    );
    localStorage.setItem(`slideIndex_${id}`, JSON.stringify(index));
  };

  // Handle main div click for sliders
  const handleMainDivClick = (
    action: Feature,
    id: Feature,
    slidesLength: number
  ) => {
    if (selectedItems.includes(id)) {
      const currentIndex = currentSlides[id] || 0;
      const newIndex = (currentIndex + 1) % slidesLength;

      setCurrentSlides((prev) => ({ ...prev, [id]: newIndex }));
      localStorage.setItem(`slideIndex_${id}`, JSON.stringify(newIndex));

      if (newIndex === 0) {
        // Cycled back to the start, so turn it off
        handleUnselection(id);
        localStorage.removeItem(`slideIndex_${id}`);
        postFeatureUnselectMessage(id);
      } else {
        // Cycled to a different slide, so post that feature
        const slideText = slidesData[id]?.[newIndex]?.text || action;
        postFeatureSelectMessage(slideText);
      }
    } else {
      // Not selected, so turn it on to the first slide (index 0)
      setSelectedItems((prev) => {
        const newItems = [...prev, id];
        localStorage.setItem("selectedItems", JSON.stringify(newItems));
        return newItems;
      });
      setCurrentSlides((prev) => ({ ...prev, [id]: 0 }));
      localStorage.setItem(`slideIndex_${id}`, JSON.stringify(0));
      const firstSlideText = slidesData[id]?.[0]?.text || action;
      postFeatureSelectMessage(firstSlideText);
    }
  };

  // Render slider component for items with multiple states
  const renderSlider = (
    id: Feature,
    slides: { text: string; image: string; label: string }[]
  ) => {
    const currentSlide = currentSlides[id] || 0;
    return (
      <button
        style={{ height: oversizeWidget ? "28vh" : "" }}
        className={clsx(
          "z-10 accessibility-card relative flex flex-col items-center justify-center rounded-lg px-7 py-4 bg-blue-[#F2F7FA] md:hover:bg-blue-200 hover:shadow-lg",
          selectedItems.includes(id)
            ? "bg-[#809be1] border-[#D9E3FC]"
            : "bg-[#F2F7FA]"
        )}
        onClick={() =>
          handleMainDivClick(
            slides[currentSlide]?.text as Feature || slides[0].text as Feature,
            id,
            slides.length
          )
        }
        aria-label={t(slides[currentSlide]?.label)}
        // Ensure button has an accessible name
      >
        {/* Tooltip Section for Slider */}
        <AccessibilityTooltipButton
          tooltipTitle="screen_reader"
          tooltipDescription="reads_selected_text_out_loud"
          tooltipAriaLabel="screen_reader_tooltip"
          shortcutKey="Alt + S"
          widgetButtonColor={widgetButtonColor}
        />
        {/* Slider Content */}
        <div className="relative cursor-pointer flex flex-col items-center">
          <button
            aria-hidden="true"
            className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
            style={{
              height: oversizeWidget ? "55px" : "",
              width: oversizeWidget ? "55px" : "",
            }}
          >
            <ScreenReader />
          </button>
          <p
            className={`text-sm text-gray-700 pt-2 pl-3 pb-3 text-center ${
              oversizeWidget ? "text-2xl font-bold" : ""
            }`}
            style={{
              fontSize: oversizeWidget ? "1.375rem" : "",
              color: selectedItems.includes(id)
                ? "white"
                : oversizeWidget
                ? "black"
                : "",
              fontWeight: oversizeWidget ? "600" : "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {t(slides[currentSlide]?.label)}
          </p>
        </div>
        {/* Slider Dots */}
        {selectedItems.includes(id) && (
          <div
            className="absolute right-0 flex flex-col space-y-2"
            style={{ top: "6px", right: "6px" }}
          >
            {slides.map((slide, dotIndex) => (
              <span
                key={dotIndex}
                className={`w-2 h-2 bg-blue rounded-full ${
                  currentSlide === dotIndex ? "bg-blue-500" : "bg-blue-100"
                } cursor-pointer`}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent the parent button's onClick from firing
                  handleDotClick(slide.text as Feature, id, dotIndex);
                }}
                role="button"
                tabIndex={0}
                aria-label={`Select ${slide.label} mode`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleDotClick(slide.text as Feature, id, dotIndex);
                  }
                }}
                style={{ transition: "none", marginLeft: "8px" }}
              />
            ))}
          </div>
        )}
      </button>
    );
  };

  // Define slides data for items with sliders
  const slidesData: {
    [key: string]: { text: Feature; image: string; label: string }[];
  } = {
    [Features.NAVIGATION_SCREEN_READER]: [
      {
        text: Features.NAVIGATION_SCREEN_READER,
        image: screenreaders,
        label: "screen_reader",
      },
      {
        text: Features.NAVIGATION_READ_FAST,
        image: screenreaders,
        label: "read_fast",
      },
      {
        text: Features.NAVIGATION_READ_SLOW,
        image: screenreaders,
        label: "read_slow",
      },
    ],
    [Features.COLOR_SATURATION]: [
      {
        text: Features.COLOR_SATURATION,
        image: "", // Add icon path here
        label: "saturation",
      },
      {
        text: Features.COLOR_HIGH_SATURATION,
        image: "", // Add icon path here
        label: "high_saturation",
      },
      {
        text: Features.COLOR_LOW_SATURATION,
        image: "", // Add icon path here
        label: "low_saturation",
      },
    ],
  };

  const resetSlides = useCallback((features: Feature[]) => {
    const newSlides = features.reduce((acc, feature) => {
      acc[feature] = 0;
      return acc;
    }, {} as { [key: string]: number });

    setCurrentSlides((prev) => ({ ...prev, ...newSlides }));
  }, []);

  const handleSelection = useCallback(
    (items: Feature | Feature[]) => {
      setSelectedItems((prev) => {
        const newItems = [...prev];
        (Array.isArray(items) ? items : [items]).forEach((item) => {
          if (!newItems.includes(item)) {
            newItems.push(item);
          }
        });
        return newItems;
      });
    },
    []
  );

  const handleUnselection = useCallback((items: Feature | Feature[]) => {
    setSelectedItems((prev) => {
      const newItems = prev.filter((i) => !((Array.isArray(items) ? items : [items]).includes(i)));
      return newItems;
    });
  }, []);

  // Effect for handling settings based on selectedCheckBox
  useEffect(() => {
    switch (selectedCheckBox.selected) {
      case Features.PROFILE_BLIND:
        handleSelection(Features.NAVIGATION_SCREEN_READER);
        postFeatureSelectMessage(Features.PROFILE_BLIND);
        break;
      case Features.PROFILE_LOW_VISION:
        handleSelection([Features.NAVIGATION_PAUSE_ANIMATION, Features.CONTENT_BIGGER_TEXT, Features.CONTENT_LEGIBLE_FONTS, Features.NAVIGATION_BIG_CURSOR, Features.COLOR_SATURATION]);
        postFeatureSelectMessage(Features.PROFILE_LOW_VISION);
        break;
        case Features.PROFILE_COLOR_BLIND:
        handleSelection([Features.COLOR_SMART_CONTRAST, Features.COLOR_SATURATION]);
        postFeatureSelectMessage(Features.PROFILE_COLOR_BLIND);
        break;
      case Features.PROFILE_SEIZURE_EPILEPTIC:
        handleSelection([Features.COLOR_LOW_SATURATION, Features.NAVIGATION_PAUSE_ANIMATION]);
        postFeatureSelectMessage(Features.PROFILE_SEIZURE_EPILEPTIC);
        break;
      case Features.PROFILE_ADHD:
        handleSelection([Features.NAVIGATION_READING_MASK, Features.COLOR_LOW_SATURATION, Features.NAVIGATION_PAUSE_ANIMATION]);
        postFeatureSelectMessage(Features.PROFILE_ADHD);
          break;
      case Features.PROFILE_MOTOR_IMPAIRED:
        handleSelection(Features.NAVIGATION_VOICE_NAVIGATION);
        postFeatureSelectMessage(Features.PROFILE_MOTOR_IMPAIRED);
        break;
      case Features.PROFILE_COGNITIVE_LEARNING:
        handleSelection([Features.CONTENT_DYSLEXIC_FRIENDLY, Features.CONTENT_HIGHLIGHT_LINKS, Features.NAVIGATION_PAUSE_ANIMATION, Features.CONTENT_BIGGER_TEXT, Features.NAVIGATION_READING_GUIDE, Features.COLOR_SMART_CONTRAST]);
        postFeatureSelectMessage(Features.PROFILE_COGNITIVE_LEARNING);
        break;
      default:
        break;
    }

    switch (selectedCheckBox.unSelected) {
      case Features.PROFILE_BLIND:
        handleUnselection(Features.NAVIGATION_SCREEN_READER);
        postFeatureUnselectMessage(Features.PROFILE_BLIND);
        break;
      case Features.PROFILE_LOW_VISION:
        handleUnselection([Features.NAVIGATION_PAUSE_ANIMATION, Features.CONTENT_BIGGER_TEXT, Features.CONTENT_LEGIBLE_FONTS, Features.NAVIGATION_BIG_CURSOR, Features.COLOR_SATURATION]);
        resetSlides([Features.COLOR_SATURATION]);
        postFeatureUnselectMessage(Features.PROFILE_LOW_VISION);
        break;
      case Features.PROFILE_COLOR_BLIND:
        handleUnselection([Features.COLOR_SMART_CONTRAST, Features.COLOR_SATURATION]);
        resetSlides([Features.COLOR_SATURATION]);
        postFeatureUnselectMessage(Features.PROFILE_COLOR_BLIND);
        break;
      case Features.PROFILE_SEIZURE_EPILEPTIC:
        handleUnselection([Features.COLOR_LOW_SATURATION, Features.NAVIGATION_PAUSE_ANIMATION]);
        resetSlides([Features.COLOR_SATURATION]);
        postFeatureUnselectMessage(Features.PROFILE_SEIZURE_EPILEPTIC);
        break;
      case Features.PROFILE_ADHD:
        handleUnselection([Features.NAVIGATION_READING_MASK, Features.COLOR_LOW_SATURATION, Features.NAVIGATION_PAUSE_ANIMATION]);
        postFeatureUnselectMessage(Features.PROFILE_ADHD);
        break;
      case Features.PROFILE_MOTOR_IMPAIRED:
        handleUnselection(Features.NAVIGATION_VOICE_NAVIGATION);
        postFeatureUnselectMessage(Features.PROFILE_MOTOR_IMPAIRED);
        break;
      case Features.PROFILE_COGNITIVE_LEARNING:
        handleUnselection([Features.CONTENT_DYSLEXIC_FRIENDLY, Features.CONTENT_HIGHLIGHT_LINKS, Features.NAVIGATION_PAUSE_ANIMATION, Features.CONTENT_BIGGER_TEXT, Features.NAVIGATION_READING_GUIDE, Features.COLOR_SMART_CONTRAST]);
        postFeatureUnselectMessage(Features.PROFILE_COGNITIVE_LEARNING);
        break;
      default:
        break;
    }
  }, [selectedCheckBox, handleSelection, handleUnselection, postFeatureSelectMessage, postFeatureUnselectMessage, resetSlides]);

  // Main render function
  return (
    <>
      <div className="rounded bg-white p-2 min-h-[100px]">
        <div
          className={twMerge(
            clsx(
              "grid gap-2",
              oversizeWidget ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"
            )
          )}
        >
          {filteredOptions.map((item) => {
            if (slidesData[item.id as string]) {
              return renderSlider(item.id as Feature, slidesData[item.id as string]);
            }
            return (
              <button
                key={item.id}
                className={twMerge(
                  clsx(
                    "px-8 cursor-pointer relative flex flex-col items-center justify-center py-4 bg-blue-[#F2F7FA] md:hover:bg-blue-200 hover:shadow-lg rounded",
                    item.id === Features.NAVIGATION_KEY_NAVIGATION
                      ? isKeyNavigationModalOpen
                        ? "bg-[#809be1] border-[#D9E3FC]"
                        : "bg-[#F2F7FA]"
                      : selectedItems.includes(item.id as Feature)
                      ? "bg-[#809be1] border-[#D9E3FC]"
                      : "bg-[#F2F7FA]"
                  )
                )}
                onClick={() => {
                  if (item.id === Features.NAVIGATION_KEY_NAVIGATION) {
                    setIsKeyNavigationModalOpen((prev) => !prev);
                  } else {
                    handleItemClick(item.id as Feature);
                  }
                }}
                style={{ height: oversizeWidget ? "28vh" : "" }}
              >
                {(item.label === "pause_animation" ||
                  item.label === "voice_navigation" ||
                  item.label === "dictionary" ||
                  item.label === "hide_images" ||
                  item.label === "big_cursor" ||
                  item.label === "reading_mask" ||
                  item.label === "dark_cursor" ||
                  item.label === "mute_sound" ||
                  item.label === "reading_guide" ||
                  item.label === "page_structure" ||
                  item.label === "key_navigation") && (
                  <AccessibilityTooltipButton
                    tooltipTitle={
                      getTooltipData(item.label)?.title || item.label
                    }
                    tooltipDescription={
                      getTooltipData(item.label)?.description || ""
                    }
                    tooltipAriaLabel={`${item.label}_tooltip`}
                    shortcutKey={getTooltipData(item.label)?.shortcutKey || ""}
                    widgetButtonColor={widgetButtonColor}
                  />
                )}

                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                  style={{
                    height: oversizeWidget ? "55px" : "",
                    width: oversizeWidget ? "55px" : "",
                  }}
                >
                  {typeof item.icon === "string" ? (
                    <img
                      src={item.icon}
                      alt={item.id as string}
                      className="w-10 h-10"
                    />
                  ) : (
                    item.icon
                  )}
                </div>

                <p
                  className={`text-sm text-gray-700 pt-2 pl-3 pb-3 text-center ${
                    oversizeWidget ? "text-2xl font-bold" : ""
                  }`}
                  style={{
                    fontSize: oversizeWidget ? "1.375rem" : "",
                    color:
                      selectedItems.includes(item.id as Feature) ||
                      (item.id === Features.NAVIGATION_KEY_NAVIGATION &&
                        isKeyNavigationModalOpen)
                        ? "white"
                        : oversizeWidget
                        ? "black"
                        : "",
                    fontWeight: oversizeWidget ? "600" : "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: oversizeWidget ? "1.5" : "normal",
                  }}
                >
                  {t(item.label)}
                </p>
              </button>
            );
          })}
        </div>

        {isContrastModalOpen && (
          <ContrastModel onClose={() => setIsContrastModalOpen(false)}>
            <form>
              <div
                className="w-full h-10 py-2 px-5 flex items-center justify-between rounded"
                style={{ backgroundColor: buttonColor }}
              >
                <p className="text-xs">{t("contrast")}</p>
                <button
                  onClick={() => setIsContrastModalOpen(false)}
                  className="font-bold text-2xl cursor-pointer"
                  style={{ color: buttonTextColor }}
                >
                  ×
                </button>
              </div>
              <div></div>
            </form>
          </ContrastModel>
        )}

        {isKeyNavigationModalOpen && (
          <KeyNavigationModel
            onClose={() => setIsKeyNavigationModalOpen(false)}
          >
            <form>
              <div
                className="w-full h-10 py-2 px-5 flex items-center justify-between text-white rounded"
                style={{ backgroundColor: buttonColor }}
              >
                <p className="text-xs">{t("key_navigation")}</p>
                <button
                  onClick={() => setIsKeyNavigationModalOpen(false)}
                  className="text-white font-bold text-2xl cursor-pointer"
                >
                  ×
                </button>
              </div>
            </form>
          </KeyNavigationModel>
        )}

        {isBlueColorModalOpen && (
          <BlueColorModel>
            <form>
              <div className="w-full h-10 py-2 px-5 flex items-center justify-between bg-blue-500 text-white rounded">
                <p className="text-xs">Blue Filter</p>
                <button
                  onClick={() => setIsBlueColorModalOpen(false)}
                  className="text-white font-bold text-2xl cursor-pointer"
                >
                  ×
                </button>
              </div>
            </form>
          </BlueColorModel>
        )}
      </div>
      <div className="h-5" />
    </>
  );
};

export default FlyoutAccessibilityGrid;
